import { AxiosError } from 'axios';
import { StatusNotHandled } from 'tyrann-io';
import { type ParsePath, z } from 'zod';

export const ApiErrorParseError = z
  .object({
    detail: z.string(),
  })
  .passthrough();

export const ApiErrorFieldErrors = z.string().array();

export const ApiErrorValidationError = z
  .object({
    non_field_errors: ApiErrorFieldErrors,
  })
  .partial()
  .passthrough();

export const isAxiosError = (error: unknown): error is AxiosError => {
  return (
    error instanceof AxiosError || !!(error && typeof error === 'object' && 'isAxiosError' in error)
  );
};

export const isAxiosErrorOfStatus = (error: unknown, status: number): error is AxiosError => {
  return isAxiosError(error) && error.response?.status === status;
};

export const displayAxiosError = (error: unknown): string | undefined => {
  if (error instanceof StatusNotHandled || isAxiosError(error)) {
    const errorMessageResult = z
      .union([
        ApiErrorParseError.transform((err) => `サーバー側からのエラー：${err.detail}`),

        ApiErrorValidationError.transform((err) => {
          const errors: { path: ParsePath; messages: string[] }[] = [];

          function collectError(value: any, path: ParsePath) {
            if (value.non_field_errors) {
              errors.push({
                path,
                messages: value.non_field_errors,
              });
            }

            for (const [k, v] of Object.entries(value)) {
              if (ApiErrorFieldErrors.safeParse(v).success) {
                errors.push({
                  path: [...path, k],
                  messages: ApiErrorFieldErrors.parse(v),
                });
              } else if (Array.isArray(v)) {
                for (let i = 0; i < v.length; i++) {
                  collectError(v[i], [...path, i]);
                }
              } else if (v && typeof v === 'object') {
                collectError(v, [...path, k]);
              }
            }
          }

          collectError(err, []);

          const messages = [];

          messages.push(`ご入力の情報に誤りがあります：`);
          for (const error of errors) {
            messages.push(
              `${error.path
                .map((key) => (typeof key === 'number' ? `第${key + 1}項` : key))
                .join('の')}: ${error.messages.length > 1 ? '\n' : ''}${error.messages.join('\n')}`,
            );
          }

          return messages.join('\n');
        }),
      ])
      .safeParse((error as AxiosError).response?.data);

    if (errorMessageResult.success) {
      return errorMessageResult.data;
    }
  }
};
